/**
 * The page an operator opens when something is wrong.
 *
 * So it leads with what is wrong. Every probe the server runs is folded into a
 * short list of problems at the top, each one naming the thing to go and look
 * at; the full readings follow underneath for the questions the summary did not
 * anticipate. A table of thirty green rows with one red cell in the middle is a
 * worse answer to "what is broken" than a sentence saying which one it is.
 *
 * The server runs each probe in isolation and substitutes an `error` string for
 * the section that failed, so a broken database does not take the version
 * numbers with it. This page reads it the same way: a missing figure is shown
 * as missing, and never as zero.
 */

import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { useAdminStatus, useAdminUpdateCheck, useCheckForUpdates } from '@/api/queries'
import type { UpdateCheck } from '@/api/types'
import { Badge, Button, Spinner } from '@/components/ui'
import type { BadgeTone } from '@/components/ui'
import { formatCapacity, formatRelative } from '@/lib/format'

import {
  AdminPage,
  Card,
  ErrorNotice,
  Fact,
  Facts,
  Notice,
  asRecord,
  readField,
  readFlag,
  readNumber,
  readNumbers,
  readText,
} from './AdminLayout'
import styles from './admin.module.css'

// ── Small formatters ────────────────────────────────────────────────────────

function plural(amount: number, unit: string): string {
  return amount === 1 ? `1 ${unit}` : `${String(amount)} ${unit}s`
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (days > 0) return `${plural(days, 'day')}, ${plural(hours, 'hour')}`
  if (hours > 0) return `${plural(hours, 'hour')}, ${plural(minutes, 'minute')}`
  return plural(minutes, 'minute')
}

function versionList(values: number[]): string {
  return values.length === 0 ? 'Not advertised' : values.map((v) => `v${String(v)}`).join(', ')
}

function count(value: number | null): string {
  return value === null ? '—' : value.toLocaleString()
}

/**
 * The server sends each storage figure as bytes and as its own rendering of
 * them, in the binary units `relaii doctor` and `docs/storage.md` use. Showing
 * the bytes through the SI `formatBytes` would put a different number under the
 * same "GB" label here than the operator sees at the terminal.
 */
function capacity(storage: unknown, field: 'used' | 'limit'): string {
  return (
    readText(readField(storage, `${field}_human`)) ??
    formatCapacity(readNumber(readField(storage, `${field}_bytes`)) ?? 0)
  )
}

const MINUTE = 60_000

// ── Problems ────────────────────────────────────────────────────────────────

interface Problem {
  id: string
  severity: 'danger' | 'warning'
  title: string
  detail: string
  link?: { to: string; label: string }
}

const SEVERITY_TONE: Record<Problem['severity'], BadgeTone> = {
  danger: 'danger',
  warning: 'warning',
}

function olderThan(timestamp: string | null, milliseconds: number): boolean {
  if (timestamp === null) return false
  const at = Date.parse(timestamp)
  return Number.isFinite(at) && Date.now() - at > milliseconds
}

/**
 * Everything worth interrupting the reader for, in the order it should be dealt
 * with. Anything that cannot be measured is left out rather than reported as a
 * failure: "not answered" and "answered badly" are different problems and only
 * one of them belongs at the top of this page.
 */
function findProblems(status: unknown): Problem[] {
  const problems: Problem[] = []

  const database = readField(status, 'database')
  if (readFlag(readField(database, 'connected')) === false) {
    problems.push({
      id: 'database',
      severity: 'danger',
      title: 'The database is not answering',
      detail:
        readText(readField(database, 'error')) ??
        'Nothing on this server works while this is true.',
    })
  } else {
    const latency = readNumber(readField(database, 'latency_ms'))
    if (latency !== null && latency > 250) {
      problems.push({
        id: 'database-latency',
        severity: 'warning',
        title: 'The database is slow to answer',
        detail: `A trivial query took ${String(latency)} ms. Anything above a few milliseconds means the pool is saturated or the disk is struggling.`,
      })
    }
  }

  const migrations = readField(status, 'migrations')
  const pending = readNumber(readField(migrations, 'pending'))
  if (readFlag(readField(migrations, 'up_to_date')) === false) {
    problems.push({
      id: 'migrations',
      severity: 'danger',
      title: 'The database schema is out of date',
      detail:
        pending === null
          ? (readText(readField(migrations, 'error')) ??
            'The migration state could not be read.')
          : `${plural(pending, 'migration')} have not been applied. The code is newer than the schema it is running against.`,
    })
  }

  const storage = readField(status, 'storage')
  const storageState = readText(readField(storage, 'state'))
  const storageError =
    readText(readField(storage, 'error')) ??
    (readText(readField(storage, 'status')) === 'error'
      ? 'The storage backend did not answer.'
      : null)

  if (storageError !== null) {
    problems.push({
      id: 'storage-error',
      severity: 'danger',
      title: 'File storage is not answering',
      detail: `${storageError} Uploads and downloads fail; messages are unaffected.`,
      link: { to: '/admin/storage', label: 'Open storage' },
    })
  } else if (storageState === 'full' || storageState === 'critical') {
    problems.push({
      id: 'storage-full',
      severity: 'danger',
      title: storageState === 'full' ? 'File storage is full' : 'File storage is nearly full',
      detail: 'Uploads are being refused, or are about to be.',
      link: { to: '/admin/storage', label: 'Open storage' },
    })
  } else if (storageState === 'warning') {
    problems.push({
      id: 'storage-warning',
      severity: 'warning',
      title: 'File storage is filling up',
      detail: 'Past three quarters of the ceiling relaii keeps for itself.',
      link: { to: '/admin/storage', label: 'Open storage' },
    })
  }

  if (readFlag(readField(status, 'smtp_configured')) === false) {
    problems.push({
      id: 'smtp',
      severity: 'warning',
      title: 'No mail relay is configured',
      detail:
        'Invitations and password resets cannot be delivered. Invitation links can still be copied out by hand.',
      link: { to: '/admin/invitations', label: 'Open invitations' },
    })
  }

  const federation = readField(status, 'federation')
  if (readFlag(readField(federation, 'enabled')) === true) {
    const peers = readNumber(readField(federation, 'peer_count'))
    const active = readNumber(readField(federation, 'active_peer_count'))

    if (peers !== null && active !== null && active < peers) {
      problems.push({
        id: 'peers',
        severity: 'warning',
        title: `${plural(peers - active, 'connected server')} not active`,
        detail:
          'A peer is waiting for approval, has been revoked, or has stopped answering this server.',
        link: { to: '/admin/federation', label: 'Open federation' },
      })
    }

    const queued = readNumber(readField(federation, 'outbox_pending')) ?? 0
    const oldest = readText(readField(federation, 'outbox_oldest_pending_at'))

    if (queued > 0 && olderThan(oldest, 15 * MINUTE)) {
      problems.push({
        id: 'outbox',
        severity: 'warning',
        title: 'Federation deliveries are backing up',
        detail: `${plural(queued, 'event')} are waiting, the oldest since ${formatRelative(
          oldest ?? '',
        )}. Nothing expires out of the queue, so a growing depth names a peer that is down.`,
        link: { to: '/admin/federation', label: 'Open federation' },
      })
    }
  }

  const queues = asRecord(readField(status, 'queues'))
  for (const [name, value] of Object.entries(queues)) {
    const retryable = readNumber(readField(value, 'retryable')) ?? 0
    if (retryable > 0) {
      problems.push({
        id: `queue-${name}`,
        severity: 'warning',
        title: `Background work is failing in the ${name} queue`,
        detail: `${plural(retryable, 'job')} have failed at least once and are waiting to be retried.`,
      })
    }
  }

  const backup = readField(status, 'backup')
  const lastBackup = readText(readField(backup, 'last_backup_at'))
  if (readText(readField(backup, 'status')) !== 'ok' || lastBackup === null) {
    problems.push({ id: 'backup-unverified', severity: 'warning', title: 'Backup protection is not verified', detail: 'No successful backup receipt is available. Verify the database, keys and attachment storage independently.' })
  }
  if (readFlag(readField(backup, 'includes_attachments')) !== true) {
    problems.push({ id: 'backup-files', severity: 'warning', title: 'Attachment backup is not verified', detail: 'The relaii backup archive does not contain uploaded file bytes. Include local storage or the object bucket in the backup plan.' })
  }
  const restored = readText(readField(backup, 'last_restore_test_at'))
  if (restored === null || olderThan(restored, 30 * 24 * 60 * MINUTE)) {
    problems.push({ id: 'restore-unverified', severity: 'warning', title: 'A recent restore test is missing', detail: 'Restore the database, keys and files on an isolated installation and record the successful check.' })
  }
  if (Object.keys(asRecord(backup)).length > 0 && olderThan(lastBackup, 48 * 60 * MINUTE)) {
    problems.push({
      id: 'backup',
      severity: 'warning',
      title: 'The last backup is old',
      detail: `The most recent backup this server knows about finished ${formatRelative(
        lastBackup ?? '',
      )}.`,
    })
  }

  return problems
}

/** A newer release is something to act on, so it joins the list of things needing attention. */
function updateProblem(update: UpdateCheck | undefined): Problem | null {
  if (update?.status !== 'update_available' || update.latest_version === null) return null

  return {
    id: 'update',
    severity: 'warning',
    title: `relaii ${update.latest_version} is available`,
    detail: `This server runs ${update.current_version}. Read the release notes, then run the update on the server when you are ready.`,
  }
}

// ── The page ────────────────────────────────────────────────────────────────

export function StatusPage(): ReactElement {
  const query = useAdminStatus()
  const updates = useAdminUpdateCheck()
  const checkForUpdates = useCheckForUpdates()
  const update = updates.data
  const status = query.data

  if (status === undefined) {
    return (
      <AdminPage title="Status">
        <Card>
          {query.isError ? (
            <ErrorNotice error={query.error} title="This server could not be asked how it is">
              <p className={styles.small}>
                The request itself failed, which is a finding in its own right: the server is
                either not running or not reachable from this browser.
              </p>
              <div className={styles.cellActions}>
                <Button onClick={() => void query.refetch()}>Try again</Button>
              </div>
            </ErrorNotice>
          ) : (
            <Spinner size="lg" label="Asking the server how it is" />
          )}
        </Card>
      </AdminPage>
    )
  }

  const problems = findProblems(status)
  const available = updateProblem(update)
  if (available !== null) problems.push(available)
  const checkedAt = readText(readField(status, 'checked_at'))

  const database = readField(status, 'database')
  const migrations = readField(status, 'migrations')
  const storage = readField(status, 'storage')
  const federation = readField(status, 'federation')
  const backup = asRecord(readField(status, 'backup'))
  const queues = asRecord(readField(status, 'queues'))

  return (
    <AdminPage
      title="Status"
      lead="What this server can and cannot currently do. Each check is taken separately, so one broken part does not hide the rest."
      actions={
        <Button onClick={() => void query.refetch()} loading={query.isFetching}>
          Check again
        </Button>
      }
    >
      {problems.length === 0 ? (
        <Notice tone="success" title="Everything checks out">
          Nothing this server measures about itself is unhealthy.
        </Notice>
      ) : (
        <Card title={`${plural(problems.length, 'thing')} needing attention`}>
          <ul className={styles.entries}>
            {problems.map((problem) => (
              <li className={styles.entry} key={problem.id}>
                <div className={styles.rowCentred}>
                  <Badge tone={SEVERITY_TONE[problem.severity]}>
                    {problem.severity === 'danger' ? 'Broken' : 'Warning'}
                  </Badge>
                  <strong>{problem.title}</strong>
                </div>
                <p className={styles.small}>{problem.detail}</p>
                {problem.link === undefined ? null : (
                  <p className={styles.small}>
                    <Link to={problem.link.to}>{problem.link.label}</Link>
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="This server">
        <Facts>
          <Fact term="relaii version">{status.product_version}</Fact>
          <Fact term="Address">
            <span className={styles.mono}>{status.server_id}</span>
          </Fact>
          <Fact term="Client API">
            {versionList(readNumbers(readField(status, 'client_api_versions')))}
          </Fact>
          <Fact term="Federation protocol">
            {versionList(readNumbers(readField(status, 'federation_versions')))}
          </Fact>
          <Fact term="Running for">{formatUptime(status.uptime_seconds)}</Fact>
          <Fact term="Checked">
            {checkedAt === null ? (
              <span className={styles.muted}>Just now</span>
            ) : (
              <span title={checkedAt}>{formatRelative(checkedAt)}</span>
            )}
          </Fact>
        </Facts>
      </Card>

      <Card title="Software updates">
        <div className={styles.stack}>
          <Facts>
            <Fact term="Running version">{status.product_version}</Fact>
            <Fact term="Available version">
              {update === undefined || update.latest_version === null ? (
                <span className={styles.muted}>Not checked yet</span>
              ) : (
                update.latest_version
              )}
            </Fact>
            {update === undefined || update.published_at === null ? null : (
              <Fact term="Released">
                <span title={update.published_at}>{formatRelative(update.published_at)}</span>
              </Fact>
            )}
            {update === undefined || update.status === 'unavailable' ? null : (
              <Fact term="Status">
                <Badge tone={update.status === 'update_available' ? 'warning' : 'success'}>
                  {update.status === 'update_available' ? 'Update available' : 'Up to date'}
                </Badge>
              </Fact>
            )}
            <Fact term="Last checked">
              {update === undefined || update.checked_at === null ? (
                <span className={styles.muted}>Never</span>
              ) : (
                <span title={update.checked_at}>{formatRelative(update.checked_at)}</span>
              )}
            </Fact>
            {update === undefined ? null : (
              <Fact term="Automatic checks">
                {update.automatic ? (
                  'Once a day'
                ) : (
                  <span className={styles.muted}>Automatic checks are off</span>
                )}
              </Fact>
            )}
          </Facts>

          {update === undefined || update.message === null ? null : (
            <p className={styles.small}>{update.message}</p>
          )}

          {checkForUpdates.isError ? (
            <ErrorNotice error={checkForUpdates.error} title="The check could not be started" />
          ) : null}

          {update?.status === 'update_available' ? (
            <Notice tone="warning" title="Update available">
              Run <code>sudo relaii update</code> on the server when you are ready. relaii never
              downloads or installs an update automatically.
            </Notice>
          ) : null}

          <div className={styles.cellActions}>
            <Button onClick={() => checkForUpdates.mutate()} loading={checkForUpdates.isPending}>
              Check for updates
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Database">
        <div className={styles.stack}>
          <Facts>
            <Fact term="Connection">
              <Connection value={readFlag(readField(database, 'connected'))} />
            </Fact>
            <Fact term="Round trip">
              {readNumber(readField(database, 'latency_ms')) === null ? (
                <span className={styles.muted}>Not measured</span>
              ) : (
                `${String(readNumber(readField(database, 'latency_ms')))} ms`
              )}
            </Fact>
            <Fact term="Schema">
              <Schema migrations={migrations} />
            </Fact>
            <Fact term="Migrations applied">
              {count(readNumber(readField(migrations, 'applied')))}
            </Fact>
            <Fact term="Latest version">
              {/* A migration stamp is an identifier, not a quantity: no grouping. */}
              {readNumber(readField(migrations, 'latest_version')) === null ? (
                <span className={styles.muted}>Unknown</span>
              ) : (
                <span className={styles.mono}>
                  {String(readNumber(readField(migrations, 'latest_version')))}
                </span>
              )}
            </Fact>
          </Facts>
          <ProbeError value={readText(readField(database, 'error'))} />
          <ProbeError value={readText(readField(migrations, 'error'))} />
        </div>
      </Card>

      <Card title="Storage" actions={<Link to="/admin/storage">Open storage</Link>}>
        <div className={styles.stack}>
          <Facts>
            <Fact term="Backend">
              {readText(readField(storage, 'backend')) ?? (
                <span className={styles.muted}>Not reported</span>
              )}
            </Fact>
            <Fact term="State">{readText(readField(storage, 'state')) ?? 'unknown'}</Fact>
            <Fact term="Stored">
              {readNumber(readField(storage, 'used_bytes')) === null ? (
                <span className={styles.muted}>Not measured</span>
              ) : (
                capacity(storage, 'used')
              )}
            </Fact>
            <Fact term="Ceiling">
              {readNumber(readField(storage, 'limit_bytes')) === null ? (
                <span className={styles.muted}>None — the provider enforces capacity</span>
              ) : (
                capacity(storage, 'limit')
              )}
            </Fact>
          </Facts>
          <ProbeError value={readText(readField(storage, 'error'))} />
        </div>
      </Card>

      <Card title="Mail">
        <Facts>
          <Fact term="Relay">
            {readFlag(readField(status, 'smtp_configured')) === true ? (
              'Configured'
            ) : (
              <>
                Not configured
                <span className={styles.hint}>
                  Invitations and password resets cannot be sent.
                </span>
              </>
            )}
          </Fact>
        </Facts>
      </Card>

      <Card title="Federation" actions={<Link to="/admin/federation">Open federation</Link>}>
        <div className={styles.stack}>
          {readFlag(readField(federation, 'enabled')) === false ? (
            <p className={styles.muted}>Federation is switched off on this server.</p>
          ) : (
            <Facts>
              <Fact term="Connected servers">
                {count(readNumber(readField(federation, 'active_peer_count')))} of{' '}
                {count(readNumber(readField(federation, 'peer_count')))} reachable
                <span className={styles.hint}>
                  A peer counts as reachable once it has been approved and is still answering.
                  This is what endpoint reachability looks like from inside this server.
                </span>
              </Fact>
              <Fact term="Waiting to be delivered">
                {count(readNumber(readField(federation, 'outbox_pending')))}
                <span className={styles.hint}>
                  Nothing expires out of the queue, so a depth that only grows names a peer that
                  is down.
                </span>
              </Fact>
              <Fact term="Oldest waiting">
                {readText(readField(federation, 'outbox_oldest_pending_at')) === null ? (
                  <span className={styles.muted}>Nothing is waiting</span>
                ) : (
                  formatRelative(readText(readField(federation, 'outbox_oldest_pending_at')) ?? '')
                )}
              </Fact>
            </Facts>
          )}
          <ProbeError value={readText(readField(federation, 'error'))} />
        </div>
      </Card>

      {Object.keys(queues).length === 0 ? null : (
        <Card
          title="Background work"
          lead="Depth per queue and per state. Jobs waiting to be retried are work that has already failed at least once."
        >
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Queue</th>
                  <th scope="col">Waiting</th>
                  <th scope="col">Scheduled</th>
                  <th scope="col">Running</th>
                  <th scope="col">Retrying</th>
                  <th scope="col">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(queues).map(([name, value]) => (
                  <tr key={name}>
                    <th scope="row">{name}</th>
                    <td className={styles.numeric}>
                      {count(readNumber(readField(value, 'available')) ?? 0)}
                    </td>
                    <td className={styles.numeric}>
                      {count(readNumber(readField(value, 'scheduled')) ?? 0)}
                    </td>
                    <td className={styles.numeric}>
                      {count(readNumber(readField(value, 'executing')) ?? 0)}
                    </td>
                    <td className={styles.numeric}>
                      {count(readNumber(readField(value, 'retryable')) ?? 0)}
                    </td>
                    <td className={styles.numeric}>
                      {count(readNumber(readField(value, 'total')) ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Runtime and security">
        <Facts>
          <Fact term="HTTP requests since restart">{count(readNumber(readField(readField(status, 'metrics'), 'requests')))}</Fact>
          <Fact term="HTTP server errors since restart">{count(readNumber(readField(readField(status, 'metrics'), 'server_errors')))}</Fact>
          <Fact term="Mean HTTP duration (ms)">{count(readNumber(readField(readField(status, 'metrics'), 'mean_duration_ms')))}</Fact>
          <Fact term="Humans without MFA">{count(readNumber(readField(readField(status, 'security'), 'unenrolled_humans')))}</Fact>
        </Facts>
        <p className={styles.muted}>Message bodies and event payloads are encrypted; the full-text search index is not encrypted.</p>
      </Card>
      <Card title="Backups">
        <p className={styles.muted}>Uploaded files require a separate backup. A database archive alone is not a complete restore.</p>
        <p className={styles.muted}>Last verified restore: {readText(backup['last_restore_test_at']) ?? 'Not recorded'}</p>
        <div className={styles.stack}>
          {Object.keys(backup).length === 0 ? (
            <p className={styles.muted}>
              This server does not report anything about backups. Whatever takes them runs outside
              relaii.
            </p>
          ) : (
            <Facts>
              <Fact term="Last backup">
                {readText(backup['last_backup_at']) === null ? (
                  <span className={styles.muted}>Never, or not recorded</span>
                ) : (
                  <span title={readText(backup['last_backup_at']) ?? undefined}>
                    {formatRelative(readText(backup['last_backup_at']) ?? '')}
                  </span>
                )}
              </Fact>
              {readText(backup['status']) === null ? null : (
                <Fact term="Result">{readText(backup['status'])}</Fact>
              )}
              {readNumber(backup['size_bytes']) === null ? null : (
                <Fact term="Size">{formatCapacity(readNumber(backup['size_bytes']) ?? 0)}</Fact>
              )}
              {readText(backup['location']) === null ? null : (
                <Fact term="Kept at">
                  <span className={styles.mono}>{readText(backup['location'])}</span>
                </Fact>
              )}
            </Facts>
          )}
          <ProbeError value={readText(backup['error'])} />
        </div>
      </Card>

      <Card title="Size of this server">
        <Facts>
          <Fact term="People">{count(readNumber(readField(status, 'user_count')))}</Fact>
          <Fact term="Rooms">{count(readNumber(readField(status, 'room_count')))}</Fact>
          <Fact term="Messages">{count(readNumber(readField(status, 'message_count')))}</Fact>
        </Facts>
      </Card>
    </AdminPage>
  )
}

function Schema({ migrations }: { migrations: unknown }): ReactElement {
  const upToDate = readFlag(readField(migrations, 'up_to_date'))
  if (upToDate === true) return <Badge tone="success">Up to date</Badge>

  const pending = readNumber(readField(migrations, 'pending'))
  if (upToDate === false) {
    return (
      <Badge tone="danger">
        {pending === null ? 'Behind the code' : `${plural(pending, 'migration')} pending`}
      </Badge>
    )
  }

  return <Badge tone="neutral">Not measured</Badge>
}

function Connection({ value }: { value: boolean | null }): ReactElement {
  if (value === true) return <Badge tone="success">Answering</Badge>
  if (value === false) return <Badge tone="danger">Not answering</Badge>
  return <Badge tone="neutral">Not measured</Badge>
}

/** A probe the server could not run. Shown where its readings would have been. */
function ProbeError({ value }: { value: string | null }): ReactElement | null {
  if (value === null) return null

  return (
    <Notice tone="danger" title="This check could not be run">
      <span className={styles.mono}>{value}</span>
    </Notice>
  )
}
